import React, { useCallback, useState } from 'react'
import { Route } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { InvoiceType, InvoiceTypeWithVehicleSales, OnboardingStepKey } from '@cbgms/api'
import { UseStepFunc, defaultStepFormProps } from '../types'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import * as routing from '../../constants/routes'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { InvoiceNumbersForm } from './InvoiceNumbersForm'
import { useStepData } from '../use-step-data'
import { FormValues } from 'app/settings/components/InvoiceSequences/Types'
import { createInvoiceSequence } from 'app/settings/state/InvoiceSequences/actions'
import { SetupWizardStepComponent } from 'app/menu/components/TopBarV1/setupWizard/SetupWizardStep'

interface StepInputProps {
  basePath: string
  stepName: OnboardingStepKey
  invoiceType: InvoiceType | InvoiceTypeWithVehicleSales
  CTAComponent: SetupWizardStepComponent
  Component: React.FC<StepProps & defaultStepFormProps<object>>
}

interface StepProps {
  basePath: string
  onSubmit: (v: FormValues) => any
  invoiceType: InvoiceType | InvoiceTypeWithVehicleSales
  stepName: OnboardingStepKey
}

export const useInvoiceNumbersStep: UseStepFunc<StepProps, StepInputProps> = ({
  basePath,
  onboardingInfo,
  stepName,
  invoiceType,
  CTAComponent,
  Component
}) => {
  const [invoiceNumbers, setInvoiceNumbers] = useState<FormValues | null>(null)

  const dispatch = useDispatch()
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)
  const stepData = useStepData(stepName, onboardingInfo)

  const onStepSubmit = (values: FormValues) => {
    setInvoiceNumbers(values)
  }

  const complete = useCompleteOnSubmit(onStepSubmit)
  const onPersist = useCallback(async () => {
    if (invoiceNumbers) {
      try {
        // Merge 3 fields to one value to build the pattern
        const pattern = invoiceNumbers.PatternYear + invoiceNumbers.PatternExtra + invoiceNumbers.PatternNumbers

        const invoiceSequence = {
          AutomatedInvoiceNumberRenewal: invoiceNumbers.AutomatedInvoiceNumberRenewal,
          NextNumber: invoiceNumbers.NextNumber,
          InvoiceType: invoiceNumbers.InvoiceType,
          Year: invoiceNumbers.Year,
          Pattern: pattern
        }

        await dispatch(createInvoiceSequence(invoiceSequence))
      } catch {
        // We cannot do anything if it fails in onPersist
      }
    }
  }, [invoiceNumbers, dispatch])

  return {
    Component,
    CTAComponent,
    StepperText: () => <StepperText stepName={stepName} />,
    isCompleted: complete.isCompleted,
    showInStepper: false,
    showStepperHeader: false,
    hideSkip: true,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    stepProps: { duplicationCheck: stepData.DuplicationCheck || [], basePath, onSubmit: complete.onSubmit, invoiceType, stepName },
    persister: {
      onPersist,
      persistOrder: 3
    }
  }
}

const StepperText: React.FC<{ stepName: OnboardingStepKey }> = ({ stepName }) => {
  const { t } = useTranslation()
  return <>{t(`onboarding.title_${stepName}`)}</>
}

export const InvoiceNumbersStep: React.FC<StepProps & defaultStepFormProps> = ({ basePath, ...rest }) => {
  return <Route path={routing.pathInvoiceNumbersStep(basePath)} render={() => <InvoiceNumbersForm {...rest} />} />
}
export const InvoiceNumbersVehiclePurchaseStep: React.FC<StepProps & defaultStepFormProps> = ({ basePath, ...rest }) => {
  return <Route path={routing.pathInvoiceSequenceVehiclePurchase(basePath)} render={() => <InvoiceNumbersForm {...rest} />} />
}
export const InvoiceNumbersVehicleSalesStep: React.FC<StepProps & defaultStepFormProps> = ({ basePath, ...rest }) => {
  return <Route path={routing.pathInvoiceSequenceVehicleSales(basePath)} render={() => <InvoiceNumbersForm {...rest} />} />
}
